import React from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

/**
 * The Axor Prism visual system — pure CSS/SVG. Multiple strategic variations.
 * No AI 3D images, no <img> for the prism.
 */

// 1 — Large 3D-style glass prism (hero primary)
export const PrismHero = ({ className }) => (
  <div className={cn("relative", className)} aria-hidden="true">
    {/* ambient glow */}
    <div className="absolute inset-0 -z-10 blur-3xl">
      <div className="absolute left-1/2 top-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-axor-cyan/25" />
    </div>
    <motion.div
      initial={{ opacity: 0, scale: 0.85, rotate: -6 }}
      animate={{ opacity: 1, scale: 1, rotate: 0 }}
      transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
      className="animate-float-slow"
    >
      <svg viewBox="0 0 400 420" className="w-full drop-shadow-[0_30px_80px_rgba(22,184,212,0.35)]">
        <defs>
          <linearGradient id="ph-left" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#2A357F" />
            <stop offset="1" stopColor="#0E1547" />
          </linearGradient>
          <linearGradient id="ph-right" x1="0.2" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#7FF0EA" />
            <stop offset="0.5" stopColor="#32D6D0" />
            <stop offset="1" stopColor="#16B8D4" />
          </linearGradient>
          <linearGradient id="ph-top" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#8DEEF0" />
            <stop offset="1" stopColor="#32D6D0" />
          </linearGradient>
          <radialGradient id="ph-refract" cx="0.5" cy="0.3" r="0.8">
            <stop offset="0" stopColor="#8DEEF0" stopOpacity="0.5" />
            <stop offset="1" stopColor="#8DEEF0" stopOpacity="0" />
          </radialGradient>
        </defs>
        {/* refracted light beams */}
        <polygon points="200,60 360,360 300,380" fill="#16B8D4" opacity="0.12" />
        <polygon points="200,60 60,360 20,340" fill="#32D6D0" opacity="0.08" />
        {/* prism faces */}
        <polygon points="200,40 40,360 200,360" fill="url(#ph-left)" />
        <polygon points="200,40 200,360 360,360" fill="url(#ph-right)" />
        <polygon points="200,40 130,196 200,196" fill="url(#ph-top)" opacity="0.55" />
        {/* glass highlight edges */}
        <line x1="200" y1="40" x2="200" y2="360" stroke="#8DEEF0" strokeWidth="1.2" opacity="0.6" />
        <line x1="200" y1="40" x2="360" y2="360" stroke="#8DEEF0" strokeWidth="1" opacity="0.4" />
        <line x1="200" y1="40" x2="40" y2="360" stroke="#ffffff" strokeWidth="0.8" opacity="0.25" />
        <ellipse cx="230" cy="150" rx="60" ry="120" fill="url(#ph-refract)" opacity="0.5" transform="rotate(20 230 150)" />
      </svg>
    </motion.div>
  </div>
);

// 2 — Floating glass chip (triangle / diamond)
export const FloatingShard = ({ className, delay = 0, shape = "triangle" }) => (
  <motion.div
    aria-hidden="true"
    className={cn("absolute", className)}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 1, delay }}
  >
    <div className="animate-float">
      <svg viewBox="0 0 60 60" className="h-full w-full">
        <defs>
          <linearGradient id={`fs-${delay}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#8DEEF0" stopOpacity="0.9" />
            <stop offset="1" stopColor="#16B8D4" stopOpacity="0.4" />
          </linearGradient>
        </defs>
        {shape === "triangle" ? (
          <polygon points="30,6 54,52 6,52" fill={`url(#fs-${delay})`} opacity="0.85" stroke="#8DEEF0" strokeWidth="0.6" strokeOpacity="0.5" />
        ) : (
          <polygon points="30,4 56,30 30,56 4,30" fill={`url(#fs-${delay})`} opacity="0.8" stroke="#8DEEF0" strokeWidth="0.6" strokeOpacity="0.5" />
        )}
      </svg>
    </div>
  </motion.div>
);

// 3 — Prism outline (line-art accent)
export const PrismOutline = ({ className, size = 120 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 100 100"
    className={cn("text-axor-cyan/40", className)}
    aria-hidden="true"
    fill="none"
  >
    <polygon points="50,8 90,80 10,80" stroke="currentColor" strokeWidth="1" />
    <line x1="50" y1="8" x2="50" y2="80" stroke="currentColor" strokeWidth="0.6" opacity="0.6" />
  </svg>
);

// 4 — Background refraction field (subtle, section-scale)
export const PrismBackground = ({ className, variant = "cyan" }) => (
  <div className={cn("pointer-events-none absolute inset-0 overflow-hidden", className)} aria-hidden="true">
    <div className="absolute -right-32 top-0 h-[500px] w-[500px] rounded-full bg-axor-cyan/[0.07] blur-[120px]" />
    <div className="absolute -left-40 bottom-0 h-[420px] w-[420px] rounded-full bg-axor-turquoise/[0.05] blur-[120px]" />
    <svg className="absolute right-10 top-1/2 h-64 w-64 -translate-y-1/2 opacity-[0.08]" viewBox="0 0 100 100">
      <polygon points="50,5 95,85 5,85" fill="none" stroke="#8DEEF0" strokeWidth="0.5" />
      <line x1="50" y1="5" x2="50" y2="85" stroke="#8DEEF0" strokeWidth="0.4" />
    </svg>
  </div>
);

// 5 — Connected prism nodes (network geometry)
export const PrismNodes = ({ className }) => (
  <svg viewBox="0 0 300 200" className={cn("w-full", className)} aria-hidden="true" fill="none">
    <defs>
      <linearGradient id="pn-line" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stopColor="#16B8D4" stopOpacity="0.1" />
        <stop offset="0.5" stopColor="#32D6D0" stopOpacity="0.6" />
        <stop offset="1" stopColor="#16B8D4" stopOpacity="0.1" />
      </linearGradient>
    </defs>
    <line x1="40" y1="40" x2="150" y2="100" stroke="url(#pn-line)" strokeWidth="1" />
    <line x1="150" y1="100" x2="260" y2="50" stroke="url(#pn-line)" strokeWidth="1" />
    <line x1="150" y1="100" x2="90" y2="160" stroke="url(#pn-line)" strokeWidth="1" />
    <line x1="150" y1="100" x2="230" y2="160" stroke="url(#pn-line)" strokeWidth="1" />
    {[
      [40, 40, 8],
      [150, 100, 12],
      [260, 50, 7],
      [90, 160, 6],
      [230, 160, 7],
    ].map(([x, y, r], i) => (
      <g key={i} className="animate-pulse-glow" style={{ animationDelay: `${i * 0.4}s` }}>
        <polygon
          points={`${x},${y - r} ${x + r},${y + r} ${x - r},${y + r}`}
          fill="#16B8D4"
          opacity="0.85"
        />
        <circle cx={x} cy={y} r={r + 6} fill="#16B8D4" opacity="0.12" />
      </g>
    ))}
  </svg>
);
